import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const empty = {
  legal_name: "", trade_name: "", cnpj: "", email: "", phone: "",
  city_ids: [], responsible_name: "", address: "",
  billing_day: 1, due_day: 10, payment_method: "boleto",
  default_percentage: 0, min_value: 0, is_active: true, notes: "",
};

export default function Agencies() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const canManage = ["admin_geral", "comercial", "administrativo"].includes(user?.role);
  const canDelete = user?.role === "admin_geral";

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/agencies"), api.get("/cities")]);
    setItems(a.data); setCities(c.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty, ...a }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form,
        default_percentage: Number(form.default_percentage),
        min_value: Number(form.min_value),
        billing_day: Number(form.billing_day),
        due_day: Number(form.due_day),
      };
      if (editing) await api.patch(`/agencies/${editing.id}`, payload);
      else await api.post("/agencies", payload);
      toast.success(editing ? "Imobiliária atualizada" : "Imobiliária criada");
      setOpen(false); load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Excluir a imobiliária ${a.trade_name}?`)) return;
    try { await api.delete(`/agencies/${a.id}`); toast.success("Imobiliária excluída"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const toggleCity = (id) => {
    setForm((f) => ({ ...f, city_ids: f.city_ids.includes(id) ? f.city_ids.filter((x) => x !== id) : [...f.city_ids, id] }));
  };

  const filtered = items.filter((a) =>
    !filter || a.trade_name.toLowerCase().includes(filter.toLowerCase()) ||
    a.legal_name?.toLowerCase().includes(filter.toLowerCase()) ||
    a.cnpj?.includes(filter)
  );

  const cityName = (id) => {
    const c = cities.find((x) => x.id === id);
    return c ? `${c.name}/${c.state}` : "—";
  };

  const chip = (active) => `px-3 py-1.5 text-xs border rounded transition-colors ${active ? "bg-[#0B1F3A] border-[#0B1F3A] text-[#FFD400] font-semibold" : "bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#0B1F3A]"}`;

  return (
    <div className="space-y-6" data-testid="agencies-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-block text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] rounded-sm">Multi-tenant</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#0B1F3A]">Imobiliárias</h1>
          <p className="text-sm text-[#6B7280] mt-1">Cada imobiliária é um ambiente isolado (tenant).</p>
        </div>
        {canManage && (
          <Button onClick={openNew} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="agencies-new-btn">
            <Plus size={16} className="mr-2" /> Nova imobiliária
          </Button>
        )}
      </div>

      <div className="flex gap-3">
        <Input placeholder="Buscar por nome ou CNPJ…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-sm bg-white" data-testid="agencies-filter-input" />
      </div>

      <div className="border border-[#E5E7EB] bg-white rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#6B7280] border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="px-4 py-3 font-bold">Imobiliária</th>
              <th className="px-4 py-3 font-bold">CNPJ</th>
              <th className="px-4 py-3 font-bold">Cidades</th>
              <th className="px-4 py-3 font-bold text-right">% padrão</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} className="border-b border-[#F1F2F4] hover:bg-[#F9FAFB]" data-testid={`agency-row-${a.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2"><Building2 size={14} className="text-[#0B1F3A]" />
                    <div>
                      <div className="font-semibold text-[#0A0A0A]">{a.trade_name}</div>
                      <div className="text-xs text-[#6B7280]">{a.legal_name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono-data text-xs text-[#0A0A0A]">{a.cnpj}</td>
                <td className="px-4 py-3 text-xs text-[#6B7280]">{(a.city_ids || []).map(cityName).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-data text-[#0A0A0A]">{a.default_percentage}%</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${a.is_active ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>
                    {a.is_active ? "Ativa" : "Inativa"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {canManage && <button onClick={() => openEdit(a)} className="text-[#6B7280] hover:text-[#0B1F3A] mr-3" data-testid={`agency-edit-${a.id}`}><Pencil size={14} /></button>}
                  {canDelete && <button onClick={() => del(a)} className="text-[#6B7280] hover:text-red-600" data-testid={`agency-delete-${a.id}`}><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#9CA3AF] text-sm">Nenhuma imobiliária encontrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#E5E7EB] text-[#0A0A0A] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-[#0B1F3A]">{editing ? "Editar imobiliária" : "Nova imobiliária"}</DialogTitle>
            <DialogDescription className="text-[#6B7280]">Dados cadastrais, cidades autorizadas e regras comerciais desta imobiliária.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Razão social</Label><Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="agency-form-legal" /></div>
            <div><Label>Nome fantasia</Label><Input value={form.trade_name} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} data-testid="agency-form-trade" /></div>
            <div><Label>CNPJ</Label><Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} data-testid="agency-form-cnpj" /></div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="agency-form-email" /></div>
            <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Responsável</Label><Input value={form.responsible_name} onChange={(e) => setForm({ ...form, responsible_name: e.target.value })} /></div>
            <div><Label>Forma de pagamento</Label><Input value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Endereço</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Dia fechamento</Label><Input type="number" value={form.billing_day} onChange={(e) => setForm({ ...form, billing_day: e.target.value })} /></div>
            <div><Label>Dia vencimento</Label><Input type="number" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} /></div>
            <div><Label>% padrão sobre locação</Label><Input type="number" step="0.01" value={form.default_percentage} onChange={(e) => setForm({ ...form, default_percentage: e.target.value })} data-testid="agency-form-percent" /></div>
            <div><Label>Valor mínimo (R$)</Label><Input type="number" step="0.01" value={form.min_value} onChange={(e) => setForm({ ...form, min_value: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <Label>Cidades autorizadas</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {cities.map((c) => (
                  <button type="button" key={c.id} onClick={() => toggleCity(c.id)}
                    className={chip(form.city_ids.includes(c.id))}
                    data-testid={`agency-city-${c.id}`}
                  >{c.name}/{c.state}</button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2"><Label>Observações</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Ativa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E5E7EB] text-[#0A0A0A] hover:bg-[#F5F6F8]">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="agency-form-save">{saving ? "Salvando…" : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
